<template>
  <div class="task-detail">
    <div class="task-detail-header">
      <div class="task-header-row">
        <div class="task-back-btn" @click="$emit('back')">
          <i class="el-icon-arrow-left"></i>
        </div>
        <h3 v-if="!editingTitle" @dblclick="startEditingTitle" class="task-title">{{ task ? task.title : 'Task Details' }}</h3>
        <el-input 
          v-else
          v-model="taskTitle"
          @blur="saveTitle"
          @keyup.enter="saveTitle"
          @keyup.esc="cancelTitleEdit"
          ref="titleInput"
          class="title-input"
        ></el-input>
        <el-tag 
          :type="getOperationTagType(task ? task.status : 'pending')" 
          size="small"
          class="task-status-top"
        >
          {{ task ? (task.status || 'pending') : 'pending' }}
        </el-tag>
      </div>
    </div>
    
    <div class="task-detail-content">
      <!-- Root Tabs for Details and Operations -->
      <el-tabs v-model="rootTab" type="card" class="root-tabs">
        <!-- Details Tab -->
        <el-tab-pane label="Details" name="details">
          <div class="details-content">
            <!-- Task Description Section -->
            <div class="detail-section">
              <h4 class="section-title">
                <i class="el-icon-document"></i>
                Description
              </h4>
              <el-input
                type="textarea"
                v-model="taskDescription"
                :rows="4"
                placeholder="Enter task description..."
                @blur="saveDescription"
                @focus="focusDescription"
                class="description-input"
              ></el-input>
            </div>

            <!-- Subtasks Section -->
            <div class="detail-section">
              <h4 class="section-title">
                <i class="el-icon-files"></i>
                Subtasks
                <el-button 
                  v-if="task && task.subtasks && task.subtasks.length > 0"
                  size="mini" 
                  type="primary" 
                  icon="el-icon-video-play"
                  @click="executeAllSubtasks"
                  class="execute-all-btn"
                  style="float: right;"
                >
                  Execute All
                </el-button>
              </h4>
              <div class="subtasks-view">
                <div v-if="task && task.subtasks && task.subtasks.length > 0">
                  <div class="subtasks-progress">
                    <el-progress 
                      :percentage="getSubtasksProgress()" 
                      :stroke-width="8"
                      class="subtasks-progress-bar"
                    ></el-progress>
                    <span class="progress-text">{{ getCompletedSubtasksCount() }}/{{ task.subtasks.length }} completed</span>
                  </div>
                  <div 
                    v-for="(subtask, index) in task.subtasks" 
                    :key="index" 
                    class="subtask-item enhanced"
                  >
                    <div class="subtask-header">
                      <el-checkbox 
                        :value="subtask.completed || subtask.status === 'completed'" 
                        @change="toggleSubtaskStatus(index, $event)"
                      >
                        <span class="subtask-title">{{ subtask.title }}</span>
                      </el-checkbox>
                      <div class="subtask-actions">
                        <el-tag 
                          size="mini" 
                          :type="getSubtaskStatusType(subtask.status)"
                          v-if="subtask.status"
                        >
                          {{ subtask.status }}
                        </el-tag>
                        <el-button 
                          size="mini" 
                          type="text" 
                          icon="el-icon-video-play"
                          @click="executeSubtask(index)"
                          v-if="subtask.status !== 'completed'"
                        >
                          Execute
                        </el-button>
                      </div>
                    </div>
                    <div v-if="subtask.description" class="subtask-description">
                      {{ subtask.description }}
                    </div>
                    <div v-if="subtask.estimated_time" class="subtask-meta">
                      <i class="el-icon-time"></i> {{ subtask.estimated_time }}
                    </div>
                  </div>
                </div>
                <p v-else class="no-subtasks">No subtasks defined</p>
                <div class="subtask-add-section">
                  <div class="subtask-input-row">
                    <el-input 
                      v-model="newSubtaskTitle" 
                      size="small"
                      placeholder="Add new subtask..."
                      @keyup.enter="addSubtaskDirect"
                      @blur="addSubtaskDirect"
                      class="new-subtask-input"
                    ></el-input>
                  </div>
                  <el-button 
                    size="mini" 
                    type="text" 
                    icon="el-icon-magic-stick"
                    @click="generateSubtasks"
                    class="generate-btn"
                  >
                    Generate Subtasks with AI
                  </el-button>
                </div>
              </div>
            </div>

            <!-- Task Metadata Section -->
            <div class="detail-section">
              <h4 class="section-title">
                <i class="el-icon-info"></i>
                Task Information
              </h4>
              <div class="metadata-grid">
                <div class="metadata-item" v-if="task && task.created_at">
                  <span class="metadata-label">Created:</span>
                  <span class="metadata-value">{{ formatDate(task.created_at) }}</span>
                </div>
                <div class="metadata-item" v-if="task && task.updated_at">
                  <span class="metadata-label">Updated:</span>
                  <span class="metadata-value">{{ formatDate(task.updated_at) }}</span>
                </div>
                <div class="metadata-item" v-if="task && task.task_type">
                  <span class="metadata-label">Type:</span>
                  <span class="metadata-value">{{ task.task_type }}</span>
                </div>
                <div class="metadata-item" v-if="task && task.priority">
                  <span class="metadata-label">Priority:</span>
                  <el-tag 
                    :type="getPriorityType(task.priority)" 
                    size="small"
                  >
                    {{ task.priority }}
                  </el-tag>
                </div>
                <div class="metadata-item" v-if="task && task.assignee">
                  <span class="metadata-label">Assignee:</span>
                  <span class="metadata-value">{{ task.assignee }}</span>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- Operations Tab -->
        <el-tab-pane label="Operations" name="operations">
          <div class="operations-tab-content">
            <!-- Task Operations List -->
            <div v-if="task && task.operations && task.operations.length > 0" class="operations-section">
        <div class="operations-header">
          <span class="operations-title">
            <i class="el-icon-s-operation"></i>
            Task Operations ({{ task.operations.length }})
          </span>
        </div>
        
        <div 
          v-for="(operation, opIndex) in task.operations" 
          :key="opIndex" 
          class="operation-card"
        >
          <div 
            class="operation-summary" 
            @click="toggleOperation(opIndex)"
            :class="{ 'expanded': expandedOperations.includes(opIndex) }"
          >
            <div class="operation-main">
              <span class="operation-number">{{ opIndex + 1 }}</span>
              <span class="operation-name">{{ operation.operation_name || `Operation ${opIndex + 1}` }}</span>
            </div>
            <div class="operation-status">
              <el-tag 
                size="small" 
                :type="getOperationTagType(operation.status)"
                effect="plain"
              >
                {{ operation.status || 'pending' }}
              </el-tag>
              <i class="expand-icon" :class="expandedOperations.includes(opIndex) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            </div>
          </div>
            
          <div v-if="expandedOperations.includes(opIndex)" class="operation-details">
            <el-tabs v-model="operationTabs[opIndex]" type="card" size="small">
              <!-- Initial Input Tab -->
              <el-tab-pane label="Initial Input" name="input">
                <div class="tab-content">
                  <div v-if="getOperationInput(operation, opIndex)">
                    <h6>Input Data</h6>
                    <!-- Handle context_pot array display -->
                    <div v-if="isContextPotArray(getOperationInput(operation, opIndex))" class="context-pot-display">
                      <div class="pot-items-header">
                        <i class="el-icon-shopping-cart-full"></i>
                        Context Items ({{ getContextPotItems(getOperationInput(operation, opIndex)).length }})
                      </div>
                      <div class="pot-items-list">
                        <div 
                          v-for="(item, potIndex) in getContextPotItems(getOperationInput(operation, opIndex))" 
                          :key="potIndex" 
                          class="pot-item-form"
                        >
                          <div class="form-header">
                            <span class="form-number">{{ potIndex + 1 }}</span>
                            <span class="form-title">{{ getItemFormTitle(item) }}</span>
                          </div>
                          <div class="form-content">
                            <table class="form-table">
                              <tbody>
                                <tr v-for="(value, key) in getItemFormFields(item)" :key="key">
                                  <td class="field-name">{{ formatFieldName(key) }}</td>
                                  <td class="field-value">
                                    <pre v-if="isSubtasksArray(value)" class="subtasks-list">{{ formatSubtasksArray(value) }}</pre>
                                    <span v-else>{{ formatValue(value) }}</span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Regular JSON table display -->
                    <div v-else-if="isValidJSON(getOperationInput(operation, opIndex))" class="json-table-container">
                      <table class="json-table">
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(value, key) in parseJSON(getOperationInput(operation, opIndex))" :key="key">
                            <td class="param-name">{{ key }}</td>
                            <td class="param-value">
                              <pre v-if="isSubtasksArray(value)" class="subtasks-list">{{ formatSubtasksArray(value) }}</pre>
                              <span v-else>{{ formatValue(value) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <!-- Plain text display -->
                    <pre v-else class="data-display">{{ getOperationInput(operation, opIndex) }}</pre>
                  </div>
                  <div v-else class="no-data">No input data</div>
                </div>
              </el-tab-pane>
                  
              <!-- Workflow Tab -->
              <el-tab-pane label="Workflow" name="workflow">
                <div class="tab-content">
                  <div v-if="operation.operation_type === 'chain' && chainData[opIndex]">
                    <div v-if="chainData[opIndex].name" class="chain-info">
                      <h6>{{ chainData[opIndex].name }}</h6>
                      <p v-if="chainData[opIndex].description" class="chain-desc">{{ chainData[opIndex].description }}</p>
                    </div>
                    
                    <div v-if="chainResults[opIndex] && chainResults[opIndex].exe_result && chainResults[opIndex].exe_result.length" class="steps-list">
                      <h6>Execution Steps</h6>
                      <div 
                        v-for="(result, resultIndex) in chainResults[opIndex].exe_result" 
                        :key="resultIndex"
                        class="step-item"
                        :class="getStepStatusClass(result)"
                      >
                        <div class="step-header">
                          <span class="step-number">{{ resultIndex + 1 }}</span>
                          <span class="step-title">{{ result.name || `Step ${resultIndex + 1}` }}</span>
                          <span v-if="result.model" class="step-model">{{ result.model }}</span>
                        </div>
                        
                        <div v-if="result.user_input" class="step-user-input-section">
                          <div class="step-section-header">
                            <i class="el-icon-user"></i> User Input
                          </div>
                          <div v-if="isValidJSON(result.user_input)" class="step-content-json">
                            <table class="mini-json-table">
                              <tbody>
                                <tr v-for="(value, key) in parseJSON(result.user_input)" :key="key">
                                  <td class="json-key">{{ key }}</td>
                                  <td class="json-value">
                                    <pre v-if="isSubtasksArray(value)" class="subtasks-list">{{ formatSubtasksArray(value) }}</pre>
                                    <span v-else>{{ formatValue(value) }}</span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <pre v-else class="step-content-text">{{ result.user_input }}</pre>
                        </div>
                        
                        <div v-if="result.content" class="step-content-section">
                          <div class="step-section-header">
                            <i class="el-icon-document"></i> Content
                          </div>
                          <div v-if="isValidJSON(result.content)" class="step-content-json">
                            <table class="mini-json-table">
                              <tbody>
                                <tr v-for="(value, key) in parseJSON(result.content)" :key="key">
                                  <td class="json-key">{{ key }}</td>
                                  <td class="json-value">
                                    <pre v-if="isSubtasksArray(value)" class="subtasks-list">{{ formatSubtasksArray(value) }}</pre>
                                    <span v-else>{{ formatValue(value) }}</span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <pre v-else class="step-content-text">{{ result.content }}</pre>
                        </div>
                        
                        <div v-if="result.res && result.res.length > 0" class="step-results-section">
                          <div class="step-section-header">
                            <i class="el-icon-success"></i> Result ({{ result.res.length }} item{{ result.res.length > 1 ? 's' : '' }})
                          </div>
                          
                          <div v-if="!result.res[0]" class="step-debug-info">
                            <el-alert type="warning" :closable="false" show-icon>
                              <span slot="title">No result data in res[0]</span>
                              <template slot="default">
                                Raw res array: <pre style="margin: 8px 0; font-size: 11px;">{{ JSON.stringify(result.res, null, 2) }}</pre>
                              </template>
                            </el-alert>
                          </div>
                          
                          <div v-else class="step-result-item">
                            <div class="step-result-status">
                              <el-tag 
                                size="small" 
                                :type="result.res[0].status === 'success' ? 'success' : result.res[0].status === 'error' ? 'danger' : 'warning'"
                                effect="plain"
                              >
                                <i :class="result.res[0].status === 'success' ? 'el-icon-check' : result.res[0].status === 'error' ? 'el-icon-close' : 'el-icon-warning'" style="margin-right: 4px;"></i>
                                {{ result.res[0].status || 'unknown' }}
                                <span v-if="result.res[0].code" style="margin-left: 8px;">• Code: {{ result.res[0].code }}</span>
                              </el-tag>
                            </div>
                            
                            <div v-if="result.res[0].msg" class="step-result-message">
                              <strong>Message:</strong> {{ result.res[0].msg }}
                            </div>
                            
                            <div v-if="result.res[0].data" class="step-result-data">
                              <div class="result-data-header">Output Data:</div>
                              <pre class="step-data-display">{{ JSON.stringify(result.res[0].data, null, 2) }}</pre>
                            </div>
                            
                            <div v-else-if="Object.keys(result.res[0]).length === 0" class="no-data-info">
                              <el-alert type="info" :closable="false" show-icon>
                                <span slot="title">Empty result object</span>
                              </el-alert>
                            </div>
                            
                            <div v-else class="step-result-raw">
                              <div class="result-data-header">Available Result Fields:</div>
                              <pre class="step-data-display">{{ JSON.stringify(result.res[0], null, 2) }}</pre>
                            </div>
                          </div>
                        </div>
                        
                        <div v-else-if="result && Object.keys(result).length > 0" class="step-debug-section">
                          <div class="step-section-header">
                            <i class="el-icon-warning"></i> Debug: No Results Array
                          </div>
                          <el-alert type="warning" :closable="false" show-icon>
                            <span slot="title">Expected 'res' array not found</span>
                            <template slot="default">
                              Available fields in result: {{ Object.keys(result).join(', ') }}
                              <pre style="margin: 8px 0; font-size: 11px; max-height: 200px; overflow-y: auto;">{{ JSON.stringify(result, null, 2) }}</pre>
                            </template>
                          </el-alert>
                        </div>
                      </div>
                    </div>
                    
                    <div v-else class="no-data">Loading chain information...</div>
                  </div>
                  <div v-else class="no-data">No workflow defined</div>
                </div>
              </el-tab-pane>
                  
              <!-- Final Output Tab -->
              <el-tab-pane label="Final Output" name="output">
                <div class="tab-content">
                      <!-- Chain Results Display (for chain operations) -->
                      <div v-if="operation.operation_type === 'chain'" class="chain-results">
                        <div v-if="chainResults[opIndex] && !chainResults[opIndex].error" class="chain-results-content">
                          <!-- Final Results Only -->
                          <div v-if="getFinalResults(chainResults[opIndex])" class="final-results-section">
                            <h5>Final Results</h5>
                            <div v-if="typeof getFinalResults(chainResults[opIndex]) === 'string'" class="final-result-item">
                              <div class="final-result-data">
                                <pre class="data-display">{{ getFinalResults(chainResults[opIndex]) }}</pre>
                              </div>
                            </div>
                            <div v-else-if="Array.isArray(getFinalResults(chainResults[opIndex]))" v-for="(finalRes, finalIndex) in getFinalResults(chainResults[opIndex])" :key="finalIndex" class="final-result-item">
                              <div v-if="typeof finalRes === 'object' && finalRes !== null">
                                <div class="final-result-header">
                                  <span class="result-index">{{ finalIndex + 1 }}</span>
                                  <el-tag v-if="finalRes.status" size="mini" :type="finalRes.status === 'success' ? 'success' : 'danger'">
                                    {{ finalRes.status }}
                                  </el-tag>
                                  <span v-if="finalRes.code" class="final-result-code">Code: {{ finalRes.code }}</span>
                                </div>
                                <div v-if="finalRes.msg" class="final-result-message">{{ finalRes.msg }}</div>
                                <div v-if="finalRes.data" class="final-result-data">
                                  <h6>Data:</h6>
                                  <pre class="data-display">{{ JSON.stringify(finalRes.data, null, 2) }}</pre>
                                </div>
                              </div>
                              <div v-else class="final-result-data">
                                <pre class="data-display">{{ formatValue(finalRes) }}</pre>
                              </div>
                            </div>
                            <div v-else class="final-result-item">
                              <div class="final-result-data">
                                <pre class="data-display">{{ formatValue(getFinalResults(chainResults[opIndex])) }}</pre>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Chain Metadata
                          <div v-if="chainResults[opIndex].chain_id || chainResults[opIndex].task_name" class="chain-metadata-section">
                            <h5>Chain Information</h5>
                            <div class="metadata-info">
                              <div v-if="chainResults[opIndex].task_name" class="metadata-item">
                                <strong>Task Name:</strong> {{ chainResults[opIndex].task_name }}
                              </div>
                              <div v-if="chainResults[opIndex].chain_id" class="metadata-item">
                                <strong>Chain ID:</strong> {{ chainResults[opIndex].chain_id }}
                              </div>
                              <div v-if="chainResults[opIndex].create_time" class="metadata-item">
                                <strong>Created:</strong> {{ formatDate(new Date(chainResults[opIndex].create_time * 1000)) }}
                              </div>
                              <div v-if="chainResults[opIndex].start_input" class="metadata-item">
                                <strong>Start Input:</strong>
                                <pre class="data-display small">{{ chainResults[opIndex].start_input }}</pre>
                              </div>
                            </div>
                          </div> -->
                        </div>
                        
                        <div v-else-if="chainResults[opIndex] && chainResults[opIndex].error" class="error-message">
                          {{ chainResults[opIndex].error }}
                        </div>
                        
                        <div v-else class="loading-message">
                          Loading chain results...
                        </div>
                      </div>
                      
                      <!-- Default Output Display (for non-chain operations) -->
                      <div v-else-if="operation.output" class="operation-output">
                        <div v-if="operation.output.result" class="output-result">
                          <h5>Result</h5>
                          <pre class="data-display">{{ JSON.stringify(operation.output.result, null, 2) }}</pre>
                        </div>
                        <div v-if="operation.output.logs" class="output-logs">
                          <h5>Logs</h5>
                          <div class="logs-container">
                            <div v-for="(log, logIndex) in operation.output.logs" :key="logIndex" class="log-entry">
                              <span class="log-time">{{ log.timestamp }}</span>
                              <span class="log-message">{{ log.message }}</span>
                            </div>
                          </div>
                        </div>
                        <div v-if="operation.output.error" class="output-error">
                          <h5>Error</h5>
                          <div class="error-message">{{ operation.output.error }}</div>
                        </div>
                      </div>
                      
                      <div v-else class="no-data">No output yet</div>
                    </div>
                  </el-tab-pane>
            </el-tabs>
          </div>
        </div>
            </div>
            
            <!-- No Operations Message -->
            <div v-else class="no-operations">
              <i class="el-icon-folder-opened"></i>
              <p>No operations available for this task</p>
              <span class="no-data-hint">Operations will appear here once the task is processed</span>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
      
      <!-- Original Task Tabs (Hidden) -->
      <!-- <el-tabs v-model="activeTab" type="card" class="task-tabs">
        <el-tab-pane label="Task Input" name="input">
          <div class="tab-content">
            <div class="input-section">
              <h4>Task Requirements</h4>
              <div class="task-description" v-if="task">
                {{ task.description }}
              </div>
              <div v-else class="placeholder">
                No task selected
              </div>
            </div>
            <div class="input-section" v-if="task">
              <h4>Input Parameters</h4>
              <div class="input-params">
                <div v-for="(param, index) in task.inputParams" :key="index" class="param-item">
                  <label>{{ param.name }}:</label>
                  <div class="param-value">{{ param.value || 'Not set' }}</div>
                </div>
                <div v-if="!task.inputParams || task.inputParams.length === 0" class="placeholder">
                  No input parameters defined
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>
        
        <el-tab-pane label="Task Workflow" name="workflow">
          <div class="tab-content">
            <div class="workflow-section">
              <h4>Process Steps</h4>
              <div v-if="task && task.workflowSteps" class="workflow-steps">
                <div 
                  v-for="(step, index) in task.workflowSteps" 
                  :key="index" 
                  class="workflow-step"
                  :class="step.status"
                >
                  <div class="step-number">{{ index + 1 }}</div>
                  <div class="step-content">
                    <div class="step-title">{{ step.title }}</div>
                    <div class="step-description">{{ step.description }}</div>
                    <div class="step-status">{{ step.status }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="placeholder">
                No workflow steps defined
              </div>
            </div>
          </div>
        </el-tab-pane>
        
        <el-tab-pane label="Task Output" name="output">
          <div class="tab-content">
            <div class="output-section">
              <h4>Task Results</h4>
              <div v-if="task && task.results" class="task-results">
                <div class="result-item" v-for="(result, index) in task.results" :key="index">
                  <div class="result-label">{{ result.label }}:</div>
                  <div class="result-content">{{ result.content }}</div>
                </div>
              </div>
              <div v-else class="placeholder">
                No results available
              </div>
            </div>
            <div class="output-section" v-if="task">
              <h4>Status & Progress</h4>
              <div class="status-info">
                <div class="status-item">
                  <span class="status-label">Status:</span>
                  <span class="status-value" :class="task.status">{{ task.status }}</span>
                </div>
                <div class="status-item" v-if="task.progress !== undefined">
                  <span class="status-label">Progress:</span>
                  <el-progress :percentage="task.progress" :status="getProgressStatus(task.status)"></el-progress>
                </div>
                <div class="status-item" v-if="task.createdAt">
                  <span class="status-label">Created:</span>
                  <span class="status-value">{{ formatDate(task.createdAt) }}</span>
                </div>
                <div class="status-item" v-if="task.completedAt">
                  <span class="status-label">Completed:</span>
                  <span class="status-value">{{ formatDate(task.completedAt) }}</span>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs> -->
    </div>
  </div>
</template>

<script>
import { postRequest } from '../service.js'

export default {
  name: "Task",
  props: {
    task: {
      type: Object,
      default: null
    }
  },
  watch: {
    task: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.taskDescription = newVal.description || '';
        }
      }
    }
  },
  data() {
    return {
      rootTab: 'operations', // Default to Operations tab
      activeTab: 'input',
      expandedOperations: [],
      operationTabs: {},
      chainData: {},
      chainResults: {},
      // Details tab editing states
      editingTitle: false,
      taskTitle: '',
      taskDescription: '',
      subtasks: [],
      newSubtaskTitle: ''
    };
  },
  methods: {
    // Subtask management methods
    getSubtasksProgress() {
      if (!this.task || !this.task.subtasks || this.task.subtasks.length === 0) return 0;
      const completed = this.task.subtasks.filter(s => s.completed || s.status === 'completed').length;
      return Math.round((completed / this.task.subtasks.length) * 100);
    },
    
    getCompletedSubtasksCount() {
      if (!this.task || !this.task.subtasks) return 0;
      return this.task.subtasks.filter(s => s.completed || s.status === 'completed').length;
    },
    
    getSubtaskStatusType(status) {
      const statusMap = {
        'pending': 'info',
        'in_progress': 'warning',
        'completed': 'success',
        'failed': 'danger'
      };
      return statusMap[status] || '';
    },
    
    async toggleSubtaskStatus(index, value) {
      try {
        const updatedSubtasks = [...this.task.subtasks];
        updatedSubtasks[index].completed = value;
        updatedSubtasks[index].status = value ? 'completed' : 'pending';
        
        const response = await postRequest('updateTask', {
          id: this.task._id?.$oid || this.task._id?.$id || this.task.id,  // Changed from 'id' to 'task_id'
          user_id: this.task.user_id,
          subtasks: updatedSubtasks
        });
        
        if (response?.data?.code === 0) {
          this.$set(this.task, 'subtasks', updatedSubtasks);
          this.$message.success('Subtask status updated');
        }
      } catch (error) {
        console.error('Error updating subtask status:', error);
        this.$message.error('Failed to update subtask status');
      }
    },
    
    async executeSubtask(index) {
      try {
        const subtask = this.task.subtasks[index];
        this.$message.info(`Executing subtask: ${subtask.title}`);
        
        // Emit event to parent to trigger AI execution
        this.$emit('execute-subtask', {
          taskId: this.task._id?.$oid || this.task._id?.$id || this.task.id,
          subtaskIndex: index,
          subtask: subtask
        });
      } catch (error) {
        console.error('Error executing subtask:', error);
        this.$message.error('Failed to execute subtask');
      }
    },
    
    async executeAllSubtasks() {
      try {
        this.$message.info('Starting automatic execution of all subtasks...');
        
        // Emit event to parent to trigger sequential execution
        this.$emit('execute-all-subtasks', {
          taskId: this.task._id?.$oid || this.task._id?.$id || this.task.id,
          subtasks: this.task.subtasks
        });
      } catch (error) {
        console.error('Error executing all subtasks:', error);
        this.$message.error('Failed to execute subtasks');
      }
    },
    
    async generateSubtasks() {
      try {
        this.$message.info('Generating subtasks with AI...');
        
        // Emit event to parent to trigger AI subtask generation
        this.$emit('generate-subtasks', {
          taskId: this.task._id?.$oid || this.task._id?.$id || this.task.id,
          taskTitle: this.task.title,
          taskDescription: this.task.description
        });
      } catch (error) {
        console.error('Error generating subtasks:', error);
        this.$message.error('Failed to generate subtasks');
      }
    },
    
    // Direct subtask add method
    async addSubtaskDirect() {
      if (!this.newSubtaskTitle.trim()) {
        return;
      }
      
      const newSubtask = {
        title: this.newSubtaskTitle,
        description: '',
        completed: false,
        status: 'pending'
      };
      
      const updatedSubtasks = [...(this.task.subtasks || []), newSubtask];
      
      try {
        const response = await postRequest('updateTask', {
          id: this.task._id?.$oid || this.task._id?.$id || this.task.id,
          user_id: this.task.user_id,
          subtasks: updatedSubtasks
        });

        if (response?.data?.code === 0) {
          this.$set(this.task, 'subtasks', updatedSubtasks);
          this.newSubtaskTitle = '';
          this.$message.success('Subtask added');
        } else {
          this.$message.error('Failed to add subtask');
        }
      } catch (error) {
        console.error('Error adding subtask:', error);
        this.$message.error('Error adding subtask');
      }
    },
    
    toggleOperation(index) {
      const idx = this.expandedOperations.indexOf(index);
      if (idx > -1) {
        this.expandedOperations.splice(idx, 1);
      } else {
        this.expandedOperations.push(index);
        // Initialize tab for this operation if not exists
        if (!this.operationTabs[index]) {
          this.$set(this.operationTabs, index, 'input');
        }
        
        // Load both chain data and chain results for chain operations
        const operation = this.task.operations[index];
        if (operation.operation_type === 'chain') {
          // Load chain data
          if (operation.chain_id && !this.chainData[index]) {
            this.loadChainData(operation.chain_id, index);
          }
          
          // Load chain results
          if (!this.chainResults[index]) {
            const taskId = this.task._id ? (task._id.$oid || task._id.$id) || this.task._id : this.task.id;
            const operationId = operation.id || (operation._id.$oid || operation._id.$id);
            
            if (taskId && operationId) {
              this.loadChainResults(taskId, operationId, index);
            }
          }
        }
      }
    },
    async loadChainData(chainId, operationIndex) {
      try {
        const response = await postRequest('getChain', { chain_id: chainId });
        // Extract the actual chain data from the response
        const chainInfo = response && response.data ? response.data : response;
        this.$set(this.chainData, operationIndex, chainInfo);
      } catch (error) {
        console.error('Failed to load chain data:', error);
        this.$set(this.chainData, operationIndex, { error: 'Failed to load chain data' });
      }
    },
    async loadChainResults(taskId, operationId, operationIndex) {
      try {
        const response = await postRequest('getChainResult', {
          task_id: taskId,
          operation_id: operationId,
          user_id: this.task.user_id
        });
        
        // Extract the actual chain results from the response
        let chainResultInfo;
        if (response && response.status === 'success' && response.data) {
          chainResultInfo = response.data;
        } else if (response && response.data) {
          chainResultInfo = response.data;
        } else {
          chainResultInfo = response;
        }
        
        this.$set(this.chainResults, operationIndex, chainResultInfo);
      } catch (error) {
        console.error('Failed to load chain results:', error);
        this.$set(this.chainResults, operationIndex, { error: 'Failed to load chain results' });
      }
    },
    onTabClick(tab, operationIndex) {
      // No longer loading data on tab click - data is loaded when operation is expanded
    },
    getStepStatusClass(result) {
      // Determine status class based on result's execution status
      if (result.res && result.res.length > 0) {
        const hasSuccess = result.res.some(r => r.status === 'success');
        const hasFailure = result.res.some(r => r.status === 'error' || r.status === 'failed');
        
        if (hasFailure) return 'failed';
        if (hasSuccess) return 'completed';
      }
      return 'pending';
    },
    getOperationTagType(status) {
      switch (status) {
        case 'completed':
        case 'success':
          return 'success';
        case 'running':
        case 'in-progress':
          return 'warning';
        case 'failed':
        case 'error':
          return 'danger';
        case 'pending':
        case 'waiting':
          return 'info';
        default:
          return 'info';
      }
    },
    getProgressStatus(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'in-progress':
          return 'active';
        case 'failed':
          return 'exception';
        default:
          return '';
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString();
    },
    isValidJSON(str) {
      try {
        JSON.parse(str);
        return true;
      } catch (e) {
        return false;
      }
    },
    parseJSON(str) {
      try {
        return JSON.parse(str);
      } catch (e) {
        return {};
      }
    },
    formatValue(value) {
      if (value === null) return 'null';
      if (value === undefined) return 'undefined';
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    },
    
    isSubtasksArray(value) {
      // Check if the value is an array of subtask objects
      if (!Array.isArray(value)) return false;
      
      // Check if it looks like subtasks (has title property)
      return value.length > 0 && value.some(item => 
        item && typeof item === 'object' && ('title' in item || 'name' in item)
      );
    },
    
    formatSubtasksArray(subtasks) {
      // Format subtasks array for display
      if (!Array.isArray(subtasks)) return '';
      
      return subtasks.map((task, index) => {
        const title = task.title || task.name || `Subtask ${index + 1}`;
        const status = task.status || task.completed ? '✅' : '⏳';
        const description = task.description ? ` - ${task.description}` : '';
        return `${index + 1}. ${status} ${title}${description}`;
      }).join('\n');
    },
    isContextPotArray(inputString) {
      try {
        const parsed = JSON.parse(inputString);
        // Check if it's an array and contains context pot-like items
        return Array.isArray(parsed) && parsed.some(item => 
          item && typeof item === 'object' 
          // (item.type || item.task || item.collection_name || item.collection_id)
        );
      } catch (e) {
        return false;
      }
    },
    getContextPotItems(inputString) {
      try {
        const parsed = JSON.parse(inputString);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    getItemFormTitle(item) {
      return item.type || item.task || item.collection_name || `Context Item`;
    },
    getItemFormFields(item) {
      // Filter out nested objects/arrays for cleaner display
      const filtered = {};
      for (const [key, value] of Object.entries(item)) {
        if (key !== 'collection_groups' && (typeof value !== 'object' || value === null)) {
          filtered[key] = value;
        }
      }
      // Add collection groups count if it exists
      if (item.collection_groups && Array.isArray(item.collection_groups)) {
        filtered.collection_groups_count = `${item.collection_groups.length} groups`;
      }
      return filtered;
    },
    formatFieldName(key) {
      // Convert snake_case to readable format
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    getFinalResults(chainResult) {
      // Handle different possible structures of chain results
      if (!chainResult) return null;
      
      // Check for data.final_results[0] structure (as mentioned in the issue)
      if (chainResult.data && chainResult.data.final_results) {
        if (Array.isArray(chainResult.data.final_results) && chainResult.data.final_results.length > 0) {
          // Return the first item if it's the only output, or the whole array
          if (chainResult.data.final_results.length === 1) {
            return chainResult.data.final_results[0];
          }
          return chainResult.data.final_results;
        }
        return chainResult.data.final_results;
      }
      
      // Fallback to check for final_result (original structure)
      if (chainResult.final_result) {
        return chainResult.final_result;
      }
      
      // Check if chainResult itself has final_results
      if (chainResult.final_results) {
        if (Array.isArray(chainResult.final_results) && chainResult.final_results.length > 0) {
          if (chainResult.final_results.length === 1) {
            return chainResult.final_results[0];
          }
          return chainResult.final_results;
        }
        return chainResult.final_results;
      }
      
      // Check if the entire response might be the result
      if (chainResult.status === 'success' && chainResult.data) {
        // If data is a string or simple value, return it directly
        if (typeof chainResult.data === 'string' || typeof chainResult.data === 'number') {
          return chainResult.data;
        }
      }
      
      return null;
    },
    // Details Tab Methods
    startEditingDescription() {
      this.editingDescription = true;
      this.taskDescription = this.task ? (this.task.description || '') : '';
    },
    
    cancelDescriptionEdit() {
      this.editingDescription = false;
      this.taskDescription = '';
    },
    
    async saveDescription() {
      try {
        const taskId = this.task._id?.$oid || this.task._id?.$id || this.task.id;

        const response = await postRequest('updateTask', {
          id: taskId,  // Changed from 'id' to 'task_id' to match backend expectation
          user_id: this.task.user_id,
          description: this.taskDescription
        });
        
        if (response?.code === 0 && response?.data) {
          // Update the entire task with the response data to ensure consistency
          const updatedTask = response.data;
          
          // Update local task object with the full updated task
          if (this.task) {
            // Preserve the original task structure but update with new data
            Object.assign(this.task, updatedTask);
          }
          
          this.editingDescription = false;
          this.$message.success('Description updated successfully');
          
          // Emit the full updated task to parent component
          this.$emit('task-updated', updatedTask);
          this.$emit('update-description', this.taskDescription);
        } else {
          // Provide more detailed error message
          const errorMsg = response?.message || 'Failed to update description';
          console.error('Update task failed:', response);
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error('Error saving description:', error);
        this.$message.error('Failed to save description: ' + (error.message || 'Unknown error'));
      }
    },
    
    // Title editing methods
    startEditingTitle() {
      this.editingTitle = true;
      this.taskTitle = this.task ? this.task.title : '';
      this.$nextTick(() => {
        this.$refs.titleInput?.focus();
      });
    },
    
    cancelTitleEdit() {
      this.editingTitle = false;
      this.taskTitle = '';
    },
    
    async saveTitle() {
      if (!this.taskTitle.trim() || this.taskTitle === this.task.title) {
        this.cancelTitleEdit();
        return;
      }
      
      try {
        const taskId = this.task._id?.$oid || this.task._id?.$id || this.task.id;
        const response = await postRequest('updateTask', {
          id: taskId,
          user_id: this.task.user_id,
          title: this.taskTitle
        });
        
        if (response?.code === 0 || response?.data?.code === 0) {
          this.$set(this.task, 'title', this.taskTitle);
          this.editingTitle = false;
          this.$message.success('Title updated');
        } else {
          this.$message.error('Failed to update title');
        }
      } catch (error) {
        console.error('Error updating title:', error);
        this.$message.error('Error updating title');
      }
    },
    
    // Description methods
    focusDescription() {
      // Called when description input is focused
    },
    
    async saveDescription() {
      if (this.taskDescription === (this.task?.description || '')) {
        return;
      }
      
      try {
        const taskId = this.task._id?.$oid || this.task._id?.$id || this.task.id;
        const response = await postRequest('updateTask', {
          id: taskId,
          user_id: this.task.user_id,
          description: this.taskDescription
        });
        
        if (response?.code === 0 || response?.data?.code === 0) {
          this.$set(this.task, 'description', this.taskDescription);
          this.$message.success('Description updated');
        } else {
          this.$message.error('Failed to update description');
        }
      } catch (error) {
        console.error('Error updating description:', error);
        this.$message.error('Error updating description');
      }
    },
    
    getPriorityType(priority) {
      const priorityMap = {
        'low': 'info',
        'medium': 'warning',
        'high': 'danger',
        'urgent': 'danger'
      };
      return priorityMap[priority] || 'info';
    },
    
    getOperationInput(operation, operationIndex) {
      // For chain operations, prefer start_input from chainResults
      if (operation.operation_type === 'chain' && this.chainResults[operationIndex]) {
        const chainResult = this.chainResults[operationIndex];
        
        // Check for start_input in various possible locations
        if (chainResult.start_input) {
          return chainResult.start_input;
        }
        
        // Check in data.start_input
        if (chainResult.data && chainResult.data.start_input) {
          return chainResult.data.start_input;
        }
        
        // Check for input_data or initial_input
        if (chainResult.input_data) {
          return chainResult.input_data;
        }
        
        if (chainResult.data && chainResult.data.input_data) {
          return chainResult.data.input_data;
        }
      }
      
      // Fallback to operation.input_string
      return operation.input_string || null;
    }
  }
};
</script>

<style scoped>
.task-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f5f7fa 0%, #f8fafc 100%);
  position: relative;
}

.task-detail-header {
  padding: 24px 28px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  position: relative;
  z-index: 5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.task-header-row {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
}

.task-title {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
  padding: 4px 8px;
  border-radius: 4px;
  flex: 1;
}

.task-title:hover {
  background-color: rgba(102, 126, 234, 0.05);
}

.title-input {
  flex: 1;
  max-width: 600px;
}

.task-status-top {
  margin-left: auto;
}

.task-back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.task-back-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-100%);
  transition: transform 0.3s;
}

.task-back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.task-back-btn:hover::before {
  transform: translateX(0);
}

.task-back-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.task-back-btn i {
  font-size: 18px;
  color: white;
  position: relative;
  z-index: 1;
}

.task-detail-header h3 {
  margin: 0;
  color: #1a202c;
  font-size: 20px;
  font-weight: 600;
  flex: 1;
  letter-spacing: -0.02em;
}

.task-detail-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.task-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
}

:deep(.el-tabs__content) {
  flex: 1;
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.el-tab-pane) {
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.tab-content {
  height: 100%;
  overflow-y: auto;
  padding: 20px;
}

.input-section,
.workflow-section,
.output-section {
  margin-bottom: 24px;
}

.input-section h4,
.workflow-section h4,
.output-section h4 {
  margin: 0 0 12px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.task-description {
  color: #606266;
  line-height: 1.5;
  background-color: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
}

.input-params {
  space-y: 8px;
}

.param-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.param-item label {
  color: #606266;
  font-weight: 500;
  min-width: 120px;
  margin-right: 12px;
}

.param-value {
  color: #303133;
  flex: 1;
}

.workflow-steps {
  space-y: 12px;
}

.workflow-step {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #e4e7ed;
}

.workflow-step.pending {
  background-color: #f4f4f5;
  border-left-color: #909399;
}

.workflow-step.in-progress {
  background-color: #e6f7ff;
  border-left-color: #1890ff;
}

.workflow-step.completed {
  background-color: #f6ffed;
  border-left-color: #52c41a;
}

.workflow-step.failed {
  background-color: #fff2f0;
  border-left-color: #ff4d4f;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #409eff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.step-description {
  color: #606266;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 4px;
}

.step-status {
  font-size: 12px;
  font-weight: 500;
  color: #909399;
  text-transform: uppercase;
}

.task-results {
  space-y: 12px;
}

.result-item {
  margin-bottom: 12px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.result-label {
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.result-content {
  color: #606266;
  line-height: 1.5;
}

.status-info {
  background-color: #f5f7fa;
  padding: 16px;
  border-radius: 6px;
}

.status-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.status-item:last-child {
  margin-bottom: 0;
}

.status-label {
  font-weight: 500;
  color: #606266;
  min-width: 80px;
  margin-right: 12px;
}

.status-value {
  color: #303133;
  flex: 1;
}

.status-value.pending {
  color: #909399;
}

.status-value.in-progress {
  color: #1890ff;
}

.status-value.completed {
  color: #52c41a;
}

.status-value.failed {
  color: #ff4d4f;
}

.placeholder {
  color: #c0c4cc;
  text-align: center;
  padding: 40px 20px;
  font-style: italic;
}

:deep(.el-progress) {
  flex: 1;
}

/* Operations Section - Simplified */
.operations-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  min-height: 0;
}

.operations-header {
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e9ecef;
}

.operations-title {
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.operations-title i {
  color: #6c757d;
}

.operation-card {
  background: white;
  border-bottom: 1px solid #e9ecef;
}

.operation-summary {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.operation-summary:hover {
  background-color: #f8f9fa;
}

.operation-summary.expanded {
  background-color: #e7f1ff;
  border-bottom: 1px solid #cce0ff;
}

.operation-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.operation-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.operation-name {
  font-weight: 500;
  color: #212529;
  font-size: 14px;
}

.operation-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.expand-icon {
  color: #6c757d;
  font-size: 16px;
}

.operation-details {
  border-top: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  max-height: 500px;
}

/* Simplified Tab Content */
.tab-content {
  padding: 16px;
  background: white;
  height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
}

.tab-content h6 {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chain-desc {
  color: #6c757d;
  font-size: 13px;
  margin: 8px 0 16px 0;
  line-height: 1.4;
}

/* Simplified Step List */
.steps-list h6 {
  margin: 16px 0 8px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid #e9ecef;
}

.step-item {
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.step-item:last-child {
  border-bottom: none;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e9ecef;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.step-item.completed .step-number {
  background: #d4edda;
  color: #155724;
}

.step-item.failed .step-number {
  background: #f8d7da;
  color: #721c24;
}

.step-title {
  font-weight: 500;
  color: #212529;
  font-size: 13px;
  flex: 1;
}

.step-model {
  font-size: 11px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
}

.operation-tab-content {
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  min-height: 400px;
}

.operation-input,
.operation-workflow,
.operation-output {
  font-size: 14px;
}

.operation-tab-content h5 {
  margin: 0 0 12px 0;
  color: #4a5568;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  position: relative;
  padding-left: 12px;
}

.operation-tab-content h5::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.input-parameters,
.input-data {
  margin-bottom: 16px;
}

.param-row {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.param-row:last-child {
  border-bottom: none;
}

.param-key {
  font-weight: 500;
  color: #606266;
  min-width: 120px;
  margin-right: 12px;
}

.param-value {
  color: #303133;
  flex: 1;
}

.data-display {
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  color: #2d3748;
  font-family: 'Consolas', 'Monaco', monospace;
  white-space: pre-wrap;
  word-break: break-word;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
}

.workflow-step-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
}

.workflow-step-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateX(4px);
}

.workflow-step-item.completed {
  background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
  border-color: rgba(16, 185, 129, 0.2);
  border-left: 3px solid #10b981;
}

.workflow-step-item.running {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: rgba(245, 158, 11, 0.2);
  border-left: 3px solid #f59e0b;
}

.workflow-step-item.failed {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: rgba(239, 68, 68, 0.2);
  border-left: 3px solid #ef4444;
}

.step-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.step-details {
  flex: 1;
}

.step-name {
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 4px;
  font-size: 14px;
}

.step-desc {
  font-size: 13px;
  color: #718096;
  line-height: 1.5;
}

.current-step-info {
  margin-top: 12px;
  padding: 8px;
  background-color: #e6f7ff;
  border-radius: 4px;
  font-size: 13px;
  color: #1890ff;
}

.output-result,
.output-logs,
.output-error {
  margin-bottom: 16px;
}

.logs-container {
  max-height: 200px;
  overflow-y: auto;
  background-color: #f5f7fa;
  padding: 8px;
  border-radius: 4px;
}

.log-entry {
  display: flex;
  gap: 12px;
  margin-bottom: 6px;
  font-size: 12px;
  font-family: monospace;
}

.log-time {
  color: #909399;
  min-width: 80px;
}

.log-message {
  color: #606266;
  flex: 1;
}

.error-message {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #ef4444;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
}

.no-data {
  color: #a0aec0;
  text-align: center;
  padding: 32px;
  font-style: italic;
  font-size: 14px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 8px;
  border: 1px dashed #cbd5e0;
}

/* Slide transition for operation content */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 600px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  transform: translateY(-5px);
}

.slide-enter-to,
.slide-leave {
  transform: translateY(0);
}

/* Adjust spacing for operations section */
.task-tabs {
  margin-top: 0;
}

.operation-details :deep(.el-tabs--card) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.operation-details :deep(.el-tabs--card > .el-tabs__header) {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;
  padding: 0;
  flex-shrink: 0;
}

.operation-details :deep(.el-tabs--card > .el-tabs__header .el-tabs__item) {
  border: none;
  border-right: 1px solid #dee2e6;
  background: transparent;
  color: #6c757d;
  font-size: 13px;
  padding: 8px 12px;
  height: auto;
  line-height: 1.4;
}

.operation-details :deep(.el-tabs--card > .el-tabs__header .el-tabs__item.is-active) {
  background: white;
  color: #007bff;
  border-bottom: 2px solid #007bff;
}

.operation-details :deep(.el-tabs__content) {
  flex: 1;
  padding: 0;
  overflow: hidden;
  min-height: 0;
}

.operation-details :deep(.el-tab-pane) {
  height: 100%;
}

/* Chain Workflow Styles */
.chain-workflow {
  font-size: 14px;
}

.chain-header {
  margin-bottom: 16px;
}

.chain-basic-info {
  margin-top: 8px;
}

.chain-name,
.chain-description {
  margin-bottom: 8px;
  padding: 6px 0;
}

.chain-steps {
  margin-top: 16px;
}

.chain-steps h6 {
  margin: 0 0 12px 0;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chain-step {
  border-left: 4px solid transparent;
  background: white;
  position: relative;
  padding-left: 16px;
  transition: all 0.3s ease;
}

.chain-step::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
}

.chain-step.completed::before {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.chain-step.failed::before {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.chain-step.pending::before {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.chain-step:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.step-type {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
  font-style: italic;
}

.chain-records,
.chain-metadata {
  margin-top: 16px;
}

.chain-records h6,
.chain-metadata h6 {
  margin: 0 0 12px 0;
  color: #606266;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chain-record-item {
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
  border-left: 3px solid #909399;
}

.record-details {
  font-size: 13px;
}

.record-name {
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.record-status {
  color: #606266;
  margin-bottom: 2px;
}

.record-time {
  color: #909399;
  font-size: 12px;
}

.metadata-info {
  background-color: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
}

.metadata-item {
  margin-bottom: 8px;
  font-size: 13px;
}

.metadata-item:last-child {
  margin-bottom: 0;
}

.loading-message {
  color: #64748b;
  text-align: center;
  padding: 32px;
  font-style: normal;
  font-size: 14px;
  position: relative;
}

.loading-message::before {
  content: '';
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-right: 8px;
  border: 2px solid #e2e8f0;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* JSON Table Styles */
.json-table-container {
  margin-top: 8px;
}

.json-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.json-table th,
.json-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.json-table th {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  font-weight: 600;
  color: #475569;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
}

.json-table tbody tr:hover {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
}

.json-table tbody tr:last-child td {
  border-bottom: none;
}

.json-table .param-name {
  font-weight: 500;
  color: #475569;
  min-width: 150px;
  max-width: 200px;
  word-break: break-word;
  font-size: 13px;
}

.json-table .param-value {
  color: #1e293b;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  word-break: break-all;
  white-space: pre-wrap;
  line-height: 1.5;
}

/* Chain Results Styles */
.chain-results {
  font-size: 14px;
}

.chain-results-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.results-section {
  margin-bottom: 16px;
}

.result-item {
  background-color: white;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  transition: box-shadow 0.2s;
}

.result-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.result-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.result-index {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #409eff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.result-name {
  font-weight: 500;
  color: #303133;
  flex: 1;
}

.result-content {
  margin: 8px 0;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
}

.result-timestamp {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  text-align: right;
}

.chain-logs {
  margin-top: 16px;
}

.chain-error {
  margin-top: 16px;
}

/* Enhanced Chain Results Styles */
.result-model {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

.result-description {
  margin: 8px 0;
  padding: 8px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border-left: 3px solid #409eff;
}

.result-content h6,
.step-results h6,
.final-result-data h6 {
  margin: 8px 0 4px 0;
  color: #606266;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-results {
  margin-top: 12px;
}

.step-result-item {
  background-color: #fafbfc;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 8px;
  margin: 6px 0;
}

.step-result-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.step-result-code {
  font-size: 11px;
  color: #606266;
}

.step-result-msg {
  font-size: 12px;
  color: #303133;
  flex: 1;
}

.step-result-data {
  margin-top: 6px;
}

.final-results-section {
  margin-top: 20px;
  border-top: 2px solid #409eff;
  padding-top: 16px;
}

.final-result-item {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
  transition: all 0.3s ease;
}

.final-result-item:hover {
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.final-result-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.final-result-code {
  font-size: 11px;
  color: #606266;
}

.final-result-message {
  font-size: 13px;
  color: #303133;
  margin-bottom: 8px;
}

.final-result-data {
  margin-top: 8px;
}

.chain-metadata-section {
  margin-top: 20px;
  border-top: 1px solid #e4e7ed;
  padding-top: 16px;
}

.data-display.small {
  font-size: 11px;
  max-height: 120px;
  overflow-y: auto;
}

/* Modern scrollbar styling */
.operation-tab-content::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.operation-tab-content::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 10px;
}

.operation-tab-content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #cbd5e0 0%, #94a3b8 100%);
  border-radius: 10px;
  border: 2px solid #f1f5f9;
}

.operation-tab-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

.operation-tab-content::-webkit-scrollbar-corner {
  background: transparent;
}

/* Context Pot Display Styles */
.context-pot-display {
  margin-top: 8px;
}

.pot-items-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background-color: #ecf5ff;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #409eff;
}

.pot-items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pot-item-form {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
}

.pot-item-form:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  border-color: rgba(102, 126, 234, 0.15);
}

.form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.form-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

.form-title {
  font-weight: 500;
  color: #303133;
  font-size: 13px;
}

.form-content {
  padding: 0;
}

.form-table {
  width: 100%;
  border-collapse: collapse;
}

.form-table tbody tr:nth-child(odd) {
  background: linear-gradient(90deg, #fafbfc 0%, #f8fafc 100%);
}

.form-table tbody tr:hover {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  transition: background 0.2s ease;
}

.form-table .field-name {
  padding: 10px 14px;
  font-weight: 500;
  color: #606266;
  min-width: 150px;
  max-width: 200px;
  word-break: break-word;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
}

.form-table .field-value {
  padding: 10px 14px;
  color: #303133;
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
  white-space: pre-wrap;
  border-bottom: 1px solid #f0f0f0;
}

.form-table tbody tr:last-child .field-name,
.form-table tbody tr:last-child .field-value {
  border-bottom: none;
}

/* Expanded Step Styles for Workflow Tab */
.workflow-step-item.expanded-step {
  padding: 12px;
  margin-bottom: 12px;
}

.step-content-section {
  margin-top: 12px;
  padding: 10px;
  background-color: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.step-content-header {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-content-json {
  background-color: white;
  border-radius: 4px;
  overflow: hidden;
}

.mini-json-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.mini-json-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
}

.mini-json-table tbody tr:last-child {
  border-bottom: none;
}

.mini-json-table tbody tr:hover {
  background-color: #f5f7fa;
}

.mini-json-table .json-key {
  padding: 8px 10px;
  font-weight: 500;
  color: #606266;
  width: 30%;
  vertical-align: top;
  background-color: #fafbfc;
}

.mini-json-table .json-value {
  padding: 8px 10px;
  color: #303133;
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
  white-space: pre-wrap;
}

.step-content-text {
  background-color: white;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  color: #303133;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.step-results-section {
  margin-top: 12px;
}

.step-result-data-inline {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 10px;
  margin-top: 8px;
  border: 1px solid #e4e7ed;
}

.step-result-header-inline {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.result-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.result-msg {
  font-size: 12px;
  color: #303133;
  flex: 1;
}

.step-data-display {
  background-color: white;
  padding: 10px;
  border-radius: 4px;
  font-size: 11px;
  color: #303133;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
}

.step-execution-status {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* New styles for improved workflow step display */
.step-user-input-section,
.step-content-section,
.step-results-section {
  margin-top: 12px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e1e5eb;
}

.step-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-section-header i {
  font-size: 14px;
  color: #667eea;
}

.step-result-item {
  background: white;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid #e1e5eb;
}

.step-result-item:last-child {
  margin-bottom: 0;
}

.step-result-status {
  margin-bottom: 8px;
}

.step-result-message {
  font-size: 13px;
  color: #475569;
  margin-bottom: 8px;
  padding: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  border-left: 3px solid #667eea;
}

.step-result-message strong {
  color: #1e293b;
  font-weight: 600;
}

.step-result-data {
  margin-top: 8px;
}

.result-data-header {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Debug and improved display styles */
.step-debug-info,
.step-debug-section {
  margin-top: 12px;
}

.no-data-info {
  margin-top: 8px;
}

.step-result-raw {
  margin-top: 8px;
}

.step-data-display {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.4;
}

/* Ensure all result sections are scrollable */
.step-results-section {
  max-height: 400px;
  overflow-y: auto;
}

.step-result-item {
  max-height: none; /* Remove height restriction */
}

/* Better scrollbar for result sections */
.step-results-section::-webkit-scrollbar,
.step-data-display::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.step-results-section::-webkit-scrollbar-track,
.step-data-display::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.step-results-section::-webkit-scrollbar-thumb,
.step-data-display::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.step-results-section::-webkit-scrollbar-thumb:hover,
.step-data-display::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Add pulse animation for running status */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.el-icon-loading {
  animation: spin 1s linear infinite;
}

/* Add hover effects for interactive elements */
.operation-info {
  transition: all 0.3s ease;
}

.operation-header:hover .operation-info {
  transform: translateX(4px);
}

/* Enhanced focus states */
*:focus-visible {
  outline: 2px solid rgba(102, 126, 234, 0.5);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Root Tabs Styles */
.root-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.root-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
  padding: 0 20px;
}

.root-tabs :deep(.el-tabs__nav) {
  border: none;
}

.root-tabs :deep(.el-tabs__item) {
  height: 48px;
  line-height: 48px;
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  transition: all 0.3s ease;
  border: none;
  background: transparent;
  margin-right: 24px;
  padding: 0 8px;
}

.root-tabs :deep(.el-tabs__item:hover) {
  color: #667eea;
}

.root-tabs :deep(.el-tabs__item.is-active) {
  color: #667eea;
  background: transparent;
  border-bottom: 3px solid #667eea;
  margin-bottom: -2px;
}

.root-tabs :deep(.el-tabs__content) {
  flex: 1;
  padding: 0;
  height: 100%;
  overflow: hidden;
}

.root-tabs :deep(.el-tab-pane) {
  height: 100%;
  overflow-y: auto;
}

/* Details Tab Styles */
.details-content {
  padding: 24px;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: hidden;
}

.detail-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
}

.description-input {
  width: 100%;
  box-sizing: border-box;
}

.new-subtask-input {
  flex: 1;
}

.subtask-add-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.subtask-input-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.detail-section:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.section-title {
  margin: 0 0 16px 0;
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.section-title i {
  color: #667eea;
  font-size: 18px;
}

/* Description Styles */
.description-view {
  position: relative;
}

.description-text {
  color: #475569;
  line-height: 1.6;
  margin: 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  min-height: 60px;
}

.no-description {
  color: #94a3b8;
  font-style: italic;
  padding: 20px;
  text-align: center;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e0;
}

.edit-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.detail-section:hover .edit-btn {
  opacity: 1;
}

/* Subtasks list formatting in JSON tables */
.subtasks-list {
  margin: 0;
  padding: 8px;
  background: #f8fafc;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 500px;
}

.json-value .subtasks-list {
  display: block;
  margin-top: 4px;
}

.description-edit {
  padding: 8px 0;
}

.edit-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* Subtasks Styles */
.subtasks-view {
  position: relative;
}

.subtask-item {
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: background 0.2s ease;
}

.subtask-item:hover {
  background: #e7f1ff;
}

.subtask-item.enhanced {
  padding: 12px;
  background: #f9fafc;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  margin-bottom: 12px;
  transition: all 0.3s ease;
}

.subtask-item.enhanced:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
  background: #ffffff;
}

.subtask-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.subtask-title {
  font-weight: 500;
  color: #303133;
}

.subtask-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.subtask-description {
  margin-left: 24px;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
  padding: 8px 0;
}

.subtask-meta {
  margin-left: 24px;
  color: #909399;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.subtasks-progress {
  margin-bottom: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.subtasks-progress-bar {
  margin-bottom: 8px;
}

.progress-text {
  font-size: 12px;
  color: #606266;
  display: block;
  text-align: center;
}

.subtask-edit-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
  padding: 12px;
  background: #f9fafc;
  border-radius: 8px;
}

.subtask-edit-content {
  flex: 1;
}

.subtask-actions-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.generate-btn {
  color: #667eea !important;
}

.generate-btn:hover {
  color: #5a67d8 !important;
  background: rgba(102, 126, 234, 0.08) !important;
}

.execute-all-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
}

.no-subtasks {
  color: #94a3b8;
  font-style: italic;
  padding: 20px;
  text-align: center;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e0;
}

.subtasks-edit {
  padding: 8px 0;
}

.subtask-edit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px;
  background: #f8fafc;
  border-radius: 6px;
}

.subtask-edit-item .el-input {
  flex: 1;
}

.add-subtask-btn {
  margin: 12px 0;
}

/* Metadata Grid Styles */
.metadata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 12px 0;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.metadata-label {
  font-weight: 500;
  color: #64748b;
  font-size: 13px;
  min-width: 60px;
}

.metadata-value {
  color: #1e293b;
  font-size: 13px;
}

/* Operations Tab Content */
.operations-tab-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.no-operations {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  padding: 40px;
}

.no-operations i {
  font-size: 64px;
  margin-bottom: 16px;
  color: #cbd5e0;
}

.no-operations p {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #64748b;
}

.no-data-hint {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  max-width: 300px;
}

/* Responsive styles */
@media (max-width: 768px) {
  .task-detail-header {
    padding: 16px 20px;
  }
  
  .operations-section {
    margin: 12px;
    padding: 16px;
  }
  
  .operation-header {
    padding: 12px 16px;
  }
  
  .operation-content {
    padding: 12px;
  }
  
  .operation-tab-content {
    padding: 12px;
  }
  
  .task-header-row h3 {
    font-size: 18px;
  }
  
  .operations-header {
    font-size: 16px;
  }
}
</style>